import type { FurnitureClass } from '../enums/furniture-class.enum.js'

/**
 * The most seats a table may be given. Not a physical limit — a banquet round
 * seats twelve and a long head table more — but a typo guard: someone meaning
 * 8 and typing 88 should be stopped at the form, not discovered by a seating
 * chart.
 */
export const FURNITURE_MAX_CAPACITY_MAX = 64

/** The part of a piece that decides seating: what it is, and what it seats. */
export interface CapacityPiece {
  class: FurnitureClass
  /** Seats, for a table someone has said. Null everywhere else. */
  maxCapacity: number | null
}

/**
 * What a set of pieces seats.
 *
 * `tablesWithoutCapacity` is kept apart from `seats` rather than folded into it
 * as a zero, because the two answer different questions. A plan's seat total
 * wants the sum it can vouch for; a rule about over-seating wants to know the
 * sum is incomplete, so it can decline to judge instead of judging on a number
 * that is missing a table.
 */
export interface Seating {
  tables: number
  tablesWithoutCapacity: number
  chairs: number
  /** Seats across the tables that have a capacity set. */
  seats: number
}

/** Tally the tables, chairs and seats across `pieces`. */
export function seatingOf(pieces: Iterable<CapacityPiece>): Seating {
  const tally: Seating = { tables: 0, tablesWithoutCapacity: 0, chairs: 0, seats: 0 }
  for (const piece of pieces) {
    if (piece.class === 'Chair') {
      tally.chairs += 1
    } else if (piece.class === 'Table') {
      tally.tables += 1
      if (piece.maxCapacity === null) tally.tablesWithoutCapacity += 1
      else tally.seats += piece.maxCapacity
    }
  }
  return tally
}

/**
 * How many chairs an arrangement has beyond what its tables seat, or null when
 * there is nothing to say.
 *
 * Null covers three cases, all of them "we do not know" rather than "it fits":
 * an arrangement with no tables (a row of ceremony chairs seats people without
 * a table in sight), one whose tables have no capacity set, and one that is
 * within its seats. A group's chairs are not tied to a particular table — they
 * are pieces at coordinates — so the sum is the only thing that can be compared,
 * and a sum missing one table's contribution would reject arrangements that fit.
 */
export function chairsOverCapacity(seating: Seating): number | null {
  if (seating.tables === 0 || seating.tablesWithoutCapacity > 0) return null
  const over = seating.chairs - seating.seats
  return over > 0 ? over : null
}

/** The refusal shown for an over-seated arrangement, on the form and from the API. */
export function overCapacityMessage(seating: Seating, over: number): string {
  const chairs = `${seating.chairs} ${seating.chairs === 1 ? 'chair' : 'chairs'}`
  const seats = `${seating.seats} ${seating.seats === 1 ? 'seat' : 'seats'}`
  return `${chairs} is ${over} more than this arrangement's ${seats}. Raise the table's max capacity or take chairs out.`
}

/** An arrangement, as the capacity rules need to see it. */
export interface CapacityArrangement {
  id: string
  name: string
  /** Its members, resolved to the class and seat count each carries. */
  pieces: CapacityPiece[]
}

/** An arrangement holding more chairs than its tables seat. */
export interface OverSeatedArrangement {
  id: string
  name: string
  chairs: number
  seats: number
  over: number
}

/**
 * The arrangements among these that hold more chairs than their tables seat.
 *
 * The caller builds the pieces, which is what lets this answer a question about
 * a change that has not happened yet: to test a new seat count for a table,
 * substitute it while mapping the members, and the result is the arrangements
 * that count would break. Saying no to such an edit needs the list, not just a
 * yes — "some arrangement somewhere" is not something anyone can act on.
 */
export function overSeatedArrangements(arrangements: Iterable<CapacityArrangement>): OverSeatedArrangement[] {
  const over: OverSeatedArrangement[] = []
  for (const arrangement of arrangements) {
    const seating = seatingOf(arrangement.pieces)
    const excess = chairsOverCapacity(seating)
    if (excess !== null) {
      over.push({
        id: arrangement.id,
        name: arrangement.name,
        chairs: seating.chairs,
        seats: seating.seats,
        over: excess,
      })
    }
  }
  return over
}

/** How many arrangements to name before the message starts counting them. */
const NAMED_ARRANGEMENTS = 5

/**
 * The refusal shown when a table's seat count would break arrangements that
 * already exist.
 *
 * It names them, because the edit is only refusable if the person can see what
 * to go and fix — and it names the chair counts, because "Reception round (10
 * chairs)" against a proposed 4 says both what is wrong and by how much without
 * anyone opening the arrangement.
 */
export function capacityBreaksArrangementsMessage(
  itemName: string,
  seats: number,
  arrangements: OverSeatedArrangement[],
): string {
  const named = arrangements
    .slice(0, NAMED_ARRANGEMENTS)
    .map((a) => `“${a.name}” (${String(a.chairs)} ${a.chairs === 1 ? 'chair' : 'chairs'})`)
  const rest = arrangements.length - named.length
  const list = rest > 0 ? `${named.join(', ')}, and ${String(rest)} more` : named.join(', ')
  const count =
    arrangements.length === 1
      ? 'there is already an arrangement that sets'
      : `there are already ${String(arrangements.length)} arrangements that set`
  return `${itemName} would seat ${String(seats)}, but ${count} more chairs at it: ${list}. Take chairs out of those first, or give this table a higher capacity.`
}
